import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import Layout from '../components/Layout';
import { formatCurrency as fmt } from '../utils/currency';

const EMPTY = { type: 'expense', amount: '', description: '', category: '', date: '' };

const CATEGORIES = [
  { value: 'Rent',      icon: '🏠' },
  { value: 'Salaries',  icon: '👥' },
  { value: 'Utilities', icon: '⚡' },
  { value: 'Transport', icon: '🚗' },
  { value: 'Marketing', icon: '📣' },
  { value: 'Equipment', icon: '🔧' },
  { value: 'Other',     icon: '📋' },
];

const CAT_ICONS = {
  Rent: '🏠', Salaries: '👥', Utilities: '⚡',
  Transport: '🚗', Marketing: '📣', Equipment: '🔧', Other: '📋',
};
const CAT_COLORS = ['#F0B429', '#00C9B1', '#38BDF8', '#A78BFA', '#FF6B6B', '#FB7185', '#FFCD3C'];

const fmtShort = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toFixed(0);
};

// ── Styles ────────────────────────────────────────────────────────────────────
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Cabinet+Grotesk:wght@400;500;700;800;900&family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@400;600&display=swap');
.fn-wrap { font-family: 'Cabinet Grotesk', sans-serif; padding: 28px 32px; }
.fn-card {
  background: #1E2235; border: 1px solid rgba(255,255,255,0.06);
  border-radius: 18px; padding: 22px; transition: border-color 0.2s;
}
.fn-card:hover { border-color: rgba(255,255,255,0.1); }
.fn-sw {
  font-size: 11px; font-weight: 700; padding: 5px 10px; border-radius: 6px;
  cursor: pointer; color: #6B7A99; transition: all 0.15s;
  border: none; background: none; font-family: 'Cabinet Grotesk', sans-serif;
}
.fn-sw.on { background: #1E2235; color: #FFFFFF; box-shadow: 0 1px 4px rgba(0,0,0,0.3); }
.fn-sw:hover:not(.on) { color: #FFFFFF; }
.fn-ptab {
  font-size: 13px; font-weight: 700; padding: 10px 18px; cursor: pointer;
  color: #6B7A99; transition: all 0.15s; border-bottom: 3px solid transparent; margin-bottom: -1px;
}
.fn-ptab:hover { color: #FFFFFF; }
.fn-ptab.on { color: #F0B429; border-bottom-color: #F0B429; }
.fn-pl-row {
  display: flex; align-items: center; padding: 8px 6px;
  border-bottom: 1px solid rgba(255,255,255,0.03);
  cursor: pointer; transition: background 0.12s; border-radius: 6px;
}
.fn-pl-row:hover { background: rgba(255,255,255,0.03); }
.fn-subtotal {
  display: flex; align-items: center;
  background: rgba(255,255,255,0.02); border-radius: 8px;
  padding: 10px 8px; margin: 4px 0;
}
.fn-exp-item {
  display: flex; align-items: center; gap: 12px; padding: 10px;
  border-radius: 12px; cursor: pointer; transition: background 0.12s; margin-bottom: 2px;
}
.fn-exp-item:hover { background: rgba(255,255,255,0.04); }
.fn-tx {
  display: flex; align-items: center; gap: 12px; padding: 9px;
  border-radius: 10px; cursor: pointer; transition: background 0.12s; margin-bottom: 2px;
}
.fn-tx:hover { background: rgba(255,255,255,0.04); }
.fn-btn-ghost {
  display: flex; align-items: center; gap: 7px; padding: 0 16px; height: 38px;
  border-radius: 10px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.06);
  color: #6B7A99; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.15s;
  font-family: 'Cabinet Grotesk', sans-serif;
}
.fn-btn-ghost:hover { background: rgba(255,255,255,0.09); color: #FFFFFF; }
.fn-btn-gold {
  display: flex; align-items: center; gap: 7px; padding: 0 18px; height: 38px;
  border-radius: 10px; background: #F0B429; color: #0B0E17;
  font-size: 13px; font-weight: 800; cursor: pointer; border: none;
  font-family: 'Cabinet Grotesk', sans-serif; transition: all 0.15s;
}
.fn-btn-gold:hover { background: #FFCD3C; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(240,180,41,0.3); }
.fn-add-btn {
  width: 100%; padding: 11px; border-radius: 12px; margin-bottom: 12px;
  background: rgba(255,255,255,0.03); border: 1px dashed rgba(255,255,255,0.12);
  color: #6B7A99; font-size: 13px; font-weight: 700; cursor: pointer;
  font-family: 'Cabinet Grotesk', sans-serif; transition: all 0.15s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.fn-add-btn:hover { background: rgba(240,180,41,0.06); border-color: rgba(240,180,41,0.3); color: #F0B429; }
.fn-chip { font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 100px; }
.fn-chip.gold  { background: rgba(240,180,41,0.12);  color: #F0B429; }
.fn-chip.teal  { background: rgba(0,201,177,0.12);   color: #00C9B1; }
.fn-chip.coral { background: rgba(255,107,107,0.12); color: #FF6B6B; }
.fn-chip.sky   { background: rgba(56,189,248,0.12);  color: #38BDF8; }
.fn-chip.lav   { background: rgba(167,139,250,0.12); color: #A78BFA; }
.fn-sec-title {
  font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
  color: #3D4559; padding: 10px 0 6px; border-bottom: 1px solid rgba(255,255,255,0.06); margin-bottom: 4px;
}
.fn-vat-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.04);
}
.fn-vat-row:last-of-type { border-bottom: none; }
/* Modal */
.fn-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.72);
  backdrop-filter: blur(4px); z-index: 1000;
  display: flex; align-items: center; justify-content: center;
}
.fn-modal {
  background: #1E2235; border: 1px solid rgba(255,255,255,0.1);
  border-radius: 20px; padding: 28px; width: 440px; max-width: calc(100vw - 40px);
  animation: fn-pop 280ms cubic-bezier(0.34,1.56,0.64,1) forwards;
}
@keyframes fn-pop { from{opacity:0;transform:scale(0.94) translateY(8px)} to{opacity:1;transform:scale(1) translateY(0)} }
.fn-input {
  width: 100%; padding: 10px 14px;
  background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px; color: #FFFFFF; font-size: 13px;
  font-family: 'Cabinet Grotesk', sans-serif; outline: none; transition: border-color 0.15s;
}
.fn-input:focus { border-color: #F0B429; }
.fn-input option { background: #1E2235; }
.fn-label {
  font-size: 11px; font-weight: 700; color: #6B7A99; margin-bottom: 6px;
  display: block; text-transform: uppercase; letter-spacing: 0.5px;
}
`;

// ── Skel ──────────────────────────────────────────────────────────────────────
function Sk({ h = 20, w = '100%', r = 6 }) {
  return <div style={{ height: h, width: w, borderRadius: r, background: 'rgba(255,255,255,0.06)' }} />;
}

// ── Section title ─────────────────────────────────────────────────────────────
function SecTitle({ children }) {
  return <div className="fn-sec-title">{children}</div>;
}

export default function Finances() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary]           = useState(null);
  const [typeFilter, setTypeFilter]     = useState('');
  const [period, setPeriod]             = useState('month');
  const [activeTab, setActiveTab]       = useState('pl');
  const [showModal, setShowModal]       = useState(false);
  const [editing, setEditing]           = useState(null);
  const [form, setForm]                 = useState(EMPTY);
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [pageLoading, setPageLoading]   = useState(true);
  const [pageError, setPageError]       = useState('');

  // ── Data loading ────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setPageLoading(true);
    setPageError('');
    try {
      const [txRes, sumRes] = await Promise.all([
        api.get('/finances/transactions', { params: typeFilter ? { type: typeFilter } : {} }),
        api.get(`/finances/summary?period=${period}`),
      ]);
      setTransactions(txRes.data);
      setSummary(sumRes.data);
    } catch {
      setPageError('Failed to load finances. Please try again.');
    } finally {
      setPageLoading(false);
    }
  }, [typeFilter, period]);

  useEffect(() => { load(); }, [load]);

  // ── Form handlers ───────────────────────────────────────────────────────────
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setError(''); setShowModal(true); };
  const openEdit = (tx) => {
    setEditing(tx);
    setForm({
      type: tx.type, amount: tx.amount, description: tx.description,
      category: tx.category || '', date: tx.date?.split('T')[0] || '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (editing) await api.put(`/finances/transactions/${editing.id}`, payload);
      else         await api.post('/finances/transactions', payload);
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this transaction?')) return;
    await api.delete(`/finances/transactions/${id}`);
    load();
  };

  // ── Derived values ──────────────────────────────────────────────────────────
  const s = summary || {};
  const grossMarginPct = s.totalRevenue
    ? ((s.grossProfit / s.totalRevenue) * 100).toFixed(1) : '0.0';
  const netMarginPct = s.totalRevenue
    ? ((s.netProfit / s.totalRevenue) * 100).toFixed(1) : '0.0';
  const netPositive = (s.netProfit ?? 0) >= 0;

  // VAT: 15% on revenue (Ghana Revenue Authority standard)
  const VAT_RATE = 0.15;
  const vatPayable = (s.totalRevenue || 0) * VAT_RATE;

  const expEntries = Object.entries(s.expenseByCategory || {})
    .sort(([, a], [, b]) => b - a);
  const maxExpAmt = expEntries[0]?.[1] || 1;

  const TABS = [
    { id: 'pl',       label: 'P&L Statement' },
    { id: 'expenses', label: 'Expenses' },
    { id: 'vat',      label: 'VAT Summary' },
  ];

  const PERIOD_OPTS = [
    { value: 'week',  label: '7D' },
    { value: 'month', label: '30D' },
    { value: 'year',  label: '1Y' },
  ];

  const recentExpenses = transactions.filter((tx) => tx.type === 'expense').slice(0, 6);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <style>{STYLES}</style>
      <div className="fn-wrap">

        {/* ── Topbar ─────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: -1, lineHeight: 1.1, color: '#FFFFFF' }}>
              Financial{' '}
              <em style={{ fontFamily: '"Instrument Serif",serif', fontStyle: 'italic', color: '#F0B429' }}>Intelligence</em>
            </div>
            <div style={{ fontSize: 13, color: '#6B7A99', marginTop: 4 }}>
              {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 3, gap: 2 }}>
              {PERIOD_OPTS.map(({ value, label }) => (
                <button key={value} className={`fn-sw${period === value ? ' on' : ''}`} onClick={() => setPeriod(value)}>
                  {label}
                </button>
              ))}
            </div>
            <button className="fn-btn-gold" onClick={openNew}>+ Add Expense</button>
          </div>
        </div>

        {/* ── Page tabs ──────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {TABS.map(({ id, label }) => (
            <div key={id} className={`fn-ptab${activeTab === id ? ' on' : ''}`} onClick={() => setActiveTab(id)}>
              {label}
            </div>
          ))}
        </div>

        {/* ── Hero band ──────────────────────────────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg,#1B2E52 0%,#0F1E38 50%,#162840 100%)',
          border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20,
          padding: '26px 28px', marginBottom: 20,
          display: 'grid', gridTemplateColumns: 'repeat(5,1fr)',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -80, right: -80, width: 260, height: 260, borderRadius: '50%', background: 'radial-gradient(circle,rgba(240,180,41,0.12) 0%,transparent 70%)', pointerEvents: 'none' }} />
          {[
            { label: 'Gross Revenue',  val: s.totalRevenue || 0,       style: { background: 'linear-gradient(135deg,#F0B429,#FFCD3C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }, delta: null },
            { label: 'Cost of Goods',  val: s.totalCOGS || 0,          style: { color: '#FF6B6B' }, delta: null },
            { label: 'Gross Profit',   val: s.grossProfit || 0,         style: { color: '#00C9B1' }, delta: `${grossMarginPct}% margin` },
            { label: 'Operating Exp.', val: s.operatingExpenses || 0,   style: { color: '#FFFFFF' }, delta: null },
            { label: 'Net Profit',     val: s.netProfit || 0,           style: { color: netPositive ? '#A78BFA' : '#FF6B6B' }, delta: `${netMarginPct}% margin` },
          ].map(({ label, val, style: valStyle, delta }, i, arr) => (
            <div key={label} style={{
              padding: '0 20px',
              paddingLeft: i === 0 ? 0 : undefined,
              borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none',
              position: 'relative', zIndex: 1,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.5px', color: '#6B7A99', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
              {pageLoading
                ? <Sk h={26} w="75%" r={6} />
                : <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: -1.5, fontFamily: '"JetBrains Mono",monospace', lineHeight: 1, ...valStyle }}>{fmtShort(val)}</div>
              }
              {delta && (
                <div style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 700, marginTop: 6, padding: '3px 8px', borderRadius: 100, background: 'rgba(0,201,177,0.12)', color: '#00C9B1' }}>
                  {delta}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Error banner ───────────────────────────────────────────────────── */}
        {pageError && (
          <div style={{ padding: '12px 16px', background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: 12, marginBottom: 16, color: '#FF6B6B', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {pageError}
            <button onClick={load} style={{ background: 'none', border: 'none', color: '#F0B429', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Retry</button>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════ */}
        {/* TAB: P&L Statement                                                  */}
        {/* ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'pl' && (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>

            {/* P&L Statement card */}
            <div className="fn-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#FFFFFF', letterSpacing: -0.3 }}>Profit & Loss Statement</div>
                  <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>
                    {period === 'week' ? 'Last 7 days' : period === 'month' ? 'Last 30 days' : 'Last year'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span className="fn-chip teal">Statement</span>
                </div>
              </div>

              {pageLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {Array(10).fill(0).map((_, i) => <Sk key={i} h={18} r={5} />)}
                </div>
              ) : (
                <>
                  {/* Revenue */}
                  <SecTitle>Revenue</SecTitle>
                  <div className="fn-pl-row">
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#6B7A99', paddingLeft: 16 }}>Total Sales Revenue</div>
                    <div style={{ fontSize: 13, fontWeight: 800, fontFamily: '"JetBrains Mono",monospace', color: '#00C9B1' }}>{fmt(s.totalRevenue || 0)}</div>
                  </div>
                  <div className="fn-subtotal">
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 800, color: '#FFFFFF' }}>Total Revenue</div>
                    <div style={{ fontSize: 16, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#F0B429', letterSpacing: -0.5 }}>{fmt(s.totalRevenue || 0)}</div>
                  </div>

                  {/* COGS */}
                  <SecTitle>Cost of Goods Sold</SecTitle>
                  <div className="fn-pl-row">
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#6B7A99', paddingLeft: 16 }}>Product Cost (COGS)</div>
                    <div style={{ fontSize: 13, fontWeight: 800, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B' }}>{fmt(s.totalCOGS || 0)}</div>
                  </div>
                  <div className="fn-subtotal">
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 800, color: '#FFFFFF' }}>Total COGS</div>
                    <div style={{ fontSize: 16, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B', letterSpacing: -0.5 }}>{fmt(s.totalCOGS || 0)}</div>
                  </div>

                  {/* Gross Profit highlight */}
                  <div style={{ background: 'rgba(0,201,177,0.06)', border: '1px solid rgba(0,201,177,0.15)', borderRadius: 10, padding: '12px 14px', margin: '12px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#00C9B1' }}>Gross Profit</div>
                      <div style={{ fontSize: 18, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#00C9B1', letterSpacing: -0.5 }}>{fmt(s.grossProfit || 0)}</div>
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(0,201,177,0.55)', marginTop: 4 }}>Gross Margin: {grossMarginPct}%</div>
                  </div>

                  <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 0 14px' }} />

                  {/* Operating Expenses */}
                  <SecTitle>Operating Expenses</SecTitle>
                  {expEntries.map(([cat, amt]) => (
                    <div key={cat} className="fn-pl-row">
                      <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#6B7A99', paddingLeft: 16 }}>{cat}</div>
                      <div style={{ fontSize: 12, fontWeight: 800, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B' }}>{fmt(amt)}</div>
                    </div>
                  ))}
                  {expEntries.length === 0 && (
                    <div className="fn-pl-row">
                      <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#6B7A99', paddingLeft: 16 }}>No expenses recorded</div>
                      <div style={{ fontSize: 12, fontWeight: 800, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B' }}>{fmt(0)}</div>
                    </div>
                  )}
                  <div className="fn-subtotal">
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 800, color: '#FFFFFF' }}>Total OpEx</div>
                    <div style={{ fontSize: 16, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B', letterSpacing: -0.5 }}>{fmt(s.operatingExpenses || 0)}</div>
                  </div>

                  {/* Net Profit highlight */}
                  <div style={{
                    background: netPositive ? 'rgba(240,180,41,0.08)' : 'rgba(255,107,107,0.08)',
                    border: `1px solid ${netPositive ? 'rgba(240,180,41,0.25)' : 'rgba(255,107,107,0.25)'}`,
                    borderRadius: 12, padding: 14, marginTop: 8,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: netPositive ? '#F0B429' : '#FF6B6B' }}>Net Profit</div>
                      <div style={{ fontSize: 22, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: netPositive ? '#F0B429' : '#FF6B6B', letterSpacing: -1 }}>
                        {fmt(s.netProfit || 0)}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: netPositive ? 'rgba(240,180,41,0.55)' : 'rgba(255,107,107,0.55)', marginTop: 4 }}>
                      Net Margin: {netMarginPct}%
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Right column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Expense Breakdown */}
              <div className="fn-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#FFFFFF', letterSpacing: -0.3 }}>Expense Breakdown</div>
                    <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>This period</div>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#FF6B6B' }}>{fmtShort(s.operatingExpenses || 0)}</div>
                </div>
                <button className="fn-add-btn" onClick={openNew}>+ Record New Expense</button>
                {pageLoading ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {Array(4).fill(0).map((_, i) => <Sk key={i} h={42} r={10} />)}
                  </div>
                ) : expEntries.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: '#6B7A99', fontSize: 13 }}>No expenses this period</div>
                ) : expEntries.map(([cat, amt], i) => {
                  const color = CAT_COLORS[i % CAT_COLORS.length];
                  const pct = Math.round((amt / (s.operatingExpenses || 1)) * 100);
                  return (
                    <div key={cat} className="fn-exp-item">
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                        {CAT_ICONS[cat] || '📋'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF' }}>{cat}</div>
                      </div>
                      <div style={{ width: 60, height: 4, borderRadius: 100, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }}>
                        <div style={{ height: '100%', width: `${Math.min(100, (amt / maxExpAmt) * 100)}%`, borderRadius: 100, background: color }} />
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 800, fontFamily: '"JetBrains Mono",monospace', color, flexShrink: 0, width: 58, textAlign: 'right' }}>{fmtShort(amt)}</div>
                      <div style={{ fontSize: 10, color: '#6B7A99', width: 28, textAlign: 'right', flexShrink: 0 }}>{pct}%</div>
                    </div>
                  );
                })}
              </div>

              {/* Recent Expenses */}
              <div className="fn-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#FFFFFF', letterSpacing: -0.3 }}>Recent Expenses</div>
                    <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>Latest transactions</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#F0B429', fontWeight: 700, cursor: 'pointer' }} onClick={() => setActiveTab('expenses')}>
                    View All →
                  </div>
                </div>
                {pageLoading ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {Array(4).fill(0).map((_, i) => <Sk key={i} h={44} r={10} />)}
                  </div>
                ) : recentExpenses.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: '#6B7A99', fontSize: 13 }}>No expenses recorded</div>
                ) : recentExpenses.map((tx) => (
                  <div key={tx.id} className="fn-tx" onClick={() => !tx.saleId && openEdit(tx)}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,107,107,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>
                      {CAT_ICONS[tx.category] || '📋'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.description}</div>
                      <div style={{ fontSize: 10, color: '#6B7A99', marginTop: 1 }}>
                        {new Date(tx.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {tx.category || 'Uncategorized'}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 800, fontFamily: '"JetBrains Mono",monospace', color: '#6B7A99', flexShrink: 0 }}>
                      -{fmt(tx.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════ */}
        {/* TAB: Expenses (transaction table)                                   */}
        {/* ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'expenses' && (
          <div className="fn-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#FFFFFF', letterSpacing: -0.3 }}>All Transactions</div>
                <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>{transactions.length} records</div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 3, gap: 2 }}>
                  {[['', 'All'], ['income', 'Income'], ['expense', 'Expense']].map(([val, label]) => (
                    <button key={val} className={`fn-sw${typeFilter === val ? ' on' : ''}`} onClick={() => setTypeFilter(val)}>{label}</button>
                  ))}
                </div>
                <button className="fn-btn-gold" onClick={openNew}>+ Add Expense</button>
              </div>
            </div>

            {pageLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Array(6).fill(0).map((_, i) => <Sk key={i} h={52} r={10} />)}
              </div>
            ) : transactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 0', color: '#6B7A99', fontSize: 13 }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>💰</div>
                <div style={{ fontWeight: 700, marginBottom: 6, color: '#FFFFFF', fontSize: 15 }}>No transactions yet</div>
                <div style={{ fontSize: 12, marginBottom: 20 }}>Log your first operating expense</div>
                <button className="fn-btn-gold" style={{ margin: '0 auto' }} onClick={openNew}>Log first expense</button>
              </div>
            ) : transactions.map((tx) => (
              <div key={tx.id} className="fn-tx">
                <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0, background: tx.type === 'income' ? 'rgba(0,201,177,0.12)' : 'rgba(255,107,107,0.1)' }}>
                  {tx.type === 'income' ? '💵' : (CAT_ICONS[tx.category] || '📋')}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tx.description}
                    {tx.saleId && <span style={{ fontSize: 10, color: '#6B7A99', marginLeft: 6 }}>(Sale #{tx.saleId})</span>}
                  </div>
                  <div style={{ fontSize: 10, color: '#6B7A99', marginTop: 1 }}>
                    {new Date(tx.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {tx.category ? ` · ${tx.category}` : ''}
                  </div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 100, background: tx.type === 'income' ? 'rgba(0,201,177,0.12)' : 'rgba(255,107,107,0.12)', color: tx.type === 'income' ? '#00C9B1' : '#FF6B6B', flexShrink: 0, marginRight: 8 }}>
                  {tx.type}
                </span>
                <div style={{ fontSize: 13, fontWeight: 800, fontFamily: '"JetBrains Mono",monospace', color: tx.type === 'income' ? '#00C9B1' : '#6B7A99', flexShrink: 0, minWidth: 90, textAlign: 'right' }}>
                  {tx.type === 'income' ? '+' : '-'}{fmt(tx.amount)}
                </div>
                {!tx.saleId ? (
                  <div style={{ display: 'flex', gap: 6, marginLeft: 10, flexShrink: 0 }}>
                    <button onClick={() => openEdit(tx)} style={{ padding: '5px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#FFFFFF', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Edit
                    </button>
                    <button onClick={() => handleDelete(tx.id)} style={{ padding: '5px 12px', borderRadius: 8, background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.2)', color: '#FF6B6B', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Delete
                    </button>
                  </div>
                ) : (
                  <span style={{ fontSize: 10, color: '#6B7A99', marginLeft: 10, flexShrink: 0 }}>Auto-generated</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════ */}
        {/* TAB: VAT Summary                                                    */}
        {/* ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'vat' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            {/* VAT computation */}
            <div className="fn-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#FFFFFF', letterSpacing: -0.3 }}>VAT Summary</div>
                  <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>Ghana Revenue Authority · 15%</div>
                </div>
                <span className="fn-chip sky">GRA Filing</span>
              </div>

              <div style={{ padding: '12px 14px', background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: 12, marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#38BDF8', fontWeight: 700, marginBottom: 4 }}>📋 Q1 2026 Filing Due</div>
                <div style={{ fontSize: 12, color: '#6B7A99' }}>April 30, 2026 · Estimated liability based on current period data</div>
              </div>

              {[
                { label: 'Total Revenue (Taxable)',  val: fmt(s.totalRevenue || 0),  color: '#FFFFFF' },
                { label: 'Less: COGS',               val: fmt(s.totalCOGS || 0),     color: '#FF6B6B' },
                { label: 'Taxable Base',             val: fmt(s.grossProfit || 0),   color: '#FFFFFF' },
                { label: 'VAT Rate',                 val: '15%',                     color: '#38BDF8' },
              ].map(({ label, val, color }) => (
                <div key={label} className="fn-vat-row">
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#6B7A99' }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, fontFamily: '"JetBrains Mono",monospace', color }}>{val}</div>
                </div>
              ))}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 12, background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)', marginTop: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#38BDF8' }}>VAT Payable to GRA</div>
                <div style={{ fontSize: 20, fontWeight: 900, fontFamily: '"JetBrains Mono",monospace', color: '#38BDF8' }}>{fmt(vatPayable)}</div>
              </div>

              <button className="fn-btn-ghost" style={{ width: '100%', marginTop: 12, justifyContent: 'center', color: '#38BDF8', borderColor: 'rgba(56,189,248,0.25)' }}>
                📥 Download VAT Return
              </button>
            </div>

            {/* Breakdown card */}
            <div className="fn-card">
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#FFFFFF', letterSpacing: -0.3 }}>P&L to VAT Bridge</div>
                <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>How your P&L feeds the VAT calc</div>
              </div>

              {[
                { label: 'Gross Revenue',     val: s.totalRevenue || 0,       color: '#F0B429' },
                { label: '− Cost of Goods',   val: s.totalCOGS || 0,          color: '#FF6B6B' },
                { label: 'Gross Profit',      val: s.grossProfit || 0,        color: '#00C9B1' },
                { label: '− Operating Exp.',  val: s.operatingExpenses || 0,  color: '#FF6B6B' },
                { label: 'Net Profit',        val: s.netProfit || 0,          color: netPositive ? '#A78BFA' : '#FF6B6B' },
              ].map(({ label, val, color }) => (
                <div key={label} className="fn-vat-row">
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#6B7A99' }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, fontFamily: '"JetBrains Mono",monospace', color }}>{fmt(val)}</div>
                </div>
              ))}

              <div style={{ marginTop: 16, padding: 14, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 12 }}>
                <div style={{ fontSize: 11, color: '#A78BFA', fontWeight: 700, marginBottom: 6 }}>ℹ️ VAT Note</div>
                <div style={{ fontSize: 12, color: '#6B7A99', lineHeight: 1.5 }}>
                  VAT computed at 15% on total revenue per Ghana Revenue Authority standard. Consult your accountant for exempt-supply categories.
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ── Modal ──────────────────────────────────────────────────────────────── */}
      {showModal && (
        <div className="fn-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="fn-modal">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#FFFFFF' }}>{editing ? 'Edit Transaction' : 'Add Expense'}</div>
              <button onClick={() => setShowModal(false)} style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#6B7A99', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ✕
              </button>
            </div>

            {error && (
              <div style={{ padding: '10px 14px', background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: 10, color: '#FF6B6B', fontSize: 13, marginBottom: 16 }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label className="fn-label">Description *</label>
                <input className="fn-input" value={form.description} onChange={set('description')} required placeholder="e.g. Monthly rent" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label className="fn-label">Amount (GHS) *</label>
                  <input className="fn-input" type="number" step="0.01" min="0" value={form.amount} onChange={set('amount')} required placeholder="0.00" />
                </div>
                <div>
                  <label className="fn-label">Date</label>
                  <input className="fn-input" type="date" value={form.date} onChange={set('date')} />
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label className="fn-label">Category</label>
                <select className="fn-input" value={form.category} onChange={set('category')}>
                  <option value="">Select category...</option>
                  {CATEGORIES.map(({ value, icon }) => (
                    <option key={value} value={value}>{icon} {value}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '10px 18px', borderRadius: 10, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#FFFFFF', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="fn-btn-gold" style={{ paddingLeft: 24, paddingRight: 24 }}>
                  {loading
                    ? <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 14, height: 14, border: '2px solid rgba(0,0,0,0.15)', borderTopColor: '#0B0E17', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                        Saving...
                      </span>
                    : editing ? 'Save Changes' : 'Add Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
